import { LoadingOutlined } from '@ant-design/icons';
import { useGetTitle } from '@knowledge/Activity/PolicyForm/share';
import { useMount } from 'ahooks';
import { Badge, Button, message } from 'antd';
import useCrud from 'hooks/useCrud';
import { useCallback } from 'react';
import PolicyForm from 'views/Aide/Scenario/NewPolicy/PolicyForm';
import { FV } from 'views/Aide/Scenario/NewPolicy/share';
import {
  getMustacheContent,
  getMustacheExecuteContextV2,
  getSlateContent,
  getSlateExecuteContext,
} from 'views/Aide/Scenario/PolicyForm';
import { policyDetail, updatePolicy } from 'views/Aide/service';
import { Container, Footer, Header } from 'views/Setting/AutoEmit/Refund/Edit';
import { ItemSelectProvider } from '@knowledge/Activity/ActivityForm/ItemSelectProvider';
import { StoreContext } from 'views/Aide/Scenario/NewPolicy/StoreContext';
import { stores2storeScopes } from '@knowledge/Activity/share';
import { noop } from 'lodash-es';
import { getActivityDetail } from '@knowledge/service/api/activity';

export interface InitiativePolicyProps {
  policyId: number;
  platform: General.PlatformUpperCase;
  region: General.UpperCaseRegion;
  onBack: () => void;
}

function InitiativePolicy(props: InitiativePolicyProps) {
  const { policyId, platform, region, onBack } = props;

  const { t, getTitle } = useGetTitle();

  const {
    loading,
    item: { initialValues, activity } = {},
    fetch,
  } = useCrud({
    read: useCallback((policyId: number) => {
      return policyDetail(policyId)
        .then(({ policy }) => {
          const { bilingualExecuteContext, bilingualExecuteContexts, executeItemContext } = policy;
          return {
            ...policy,
            bilingualExecuteContext: getSlateExecuteContext(bilingualExecuteContext),
            bilingualExecuteContexts: bilingualExecuteContexts?.map(getSlateExecuteContext),
            executeItemContext: executeItemContext?.map((item) => {
              const { customizeContext, ...others } = item;
              return {
                customizeContext: getSlateContent(customizeContext),
                ...others,
              };
            }),
          } as FV;
        })
        .then((initialValues) => {
          return getActivityDetail(initialValues.knowledgeId as any).then((activity) => {
            return {
              initialValues,
              activity,
            };
          });
        });
    }, []),
  });

  const refresh = () => {
    return fetch(policyId).catch(() => {
      message.error(t('phrase.获取数据失败，请稍后再试'));
    });
  };

  useMount(refresh);

  if (!initialValues || !activity) {
    return loading ? <LoadingOutlined /> : null;
  }

  const form = (
    <PolicyForm
      existNameList={[]}
      initialValues={initialValues}
      scenarioType={initialValues.scenarioType}
      platform={platform}
      region={region}
      onUpdate={refresh}
      onSubmit={(values) => {
        const {
          bilingualExecuteContext,
          bilingualExecuteContexts = [],
          executeItemContext = [],
          ...others
        } = values;

        const submitValues = {
          ...others,
          bilingualExecuteContext: getMustacheExecuteContextV2(bilingualExecuteContext),
          bilingualExecuteContexts: bilingualExecuteContexts.map(getMustacheExecuteContextV2),
          executeItemContext: executeItemContext.map((item) => {
            const { customizeContext = [], ...others } = item;

            return {
              customizeContext: getMustacheContent(customizeContext),
              ...others,
            };
          }),
        };

        return updatePolicy(submitValues as any)
          .then(() => {
            message.success(t('operation.saveSucceed'));
            onBack();
          })
          .catch(() => {
            message.error(t('phrase.保存失败，请稍后再试'));
          });
      }}
    >
      {(form, props) => {
        const { dirty, isSubmitting, handleSubmit } = props;

        const { activitySendPoint, scenarioType } = initialValues as any;

        return (
          <Container>
            <Header>
              <h2>
                {`${t('marketing.编辑')}-[${getTitle(activitySendPoint, scenarioType)}] (${
                  activity.activityName
                })`}
              </h2>
            </Header>
            {form}
            <Footer>
              <Badge dot={dirty}>
                <Button type="primary" loading={isSubmitting} onClick={() => handleSubmit()}>
                  {t('phrase.保存')}
                </Button>
              </Badge>
              <Button onClick={() => onBack()}>{t('phrase.取消')}</Button>
            </Footer>
          </Container>
        );
      }}
    </PolicyForm>
  );

  return (
    <StoreContext.Provider
      value={{
        storeInfo: {
          storeScopes: stores2storeScopes(activity.stores),
        },
        setStoreInfo: noop,
      }}
    >
      <ItemSelectProvider>{form}</ItemSelectProvider>
    </StoreContext.Provider>
  );
}

export default InitiativePolicy;
